import { apiClient } from '.';

// 검색 API 상태 확인 유틸리티
export const checkSearchApiEndpoint = async (): Promise<{ isNewApiAvailable: boolean; error?: any }> => {
  try {
    // 새로운 API 엔드포인트 테스트 (빈 키워드로 간단 테스트)
    await apiClient.get(`/api/search/lecturers?keyword=test&page=0&size=1`, { withCredentials: true });
    return { isNewApiAvailable: true };
  } catch (error) {
    return { isNewApiAvailable: false, error };
  }
};

// 검색 API 관련 타입 정의
export interface LectureSearchItem {
  lecture_info: {
    id: number;
    title: string;
    thumbnail_url: string;
    tags: string[];
  };
  lecturer_info: {
    id: number;
    name: string;
    department: string;
    image_url: string;
    tags: string[];
  };
}

export interface LecturerSearchItem {
  id: number;
  name: string;
  department: string;
  image_url: string;
  tags: string[];
}

export interface PostSearchItem {
  id: number;
  title: string;
  category: string;
  replyCnt: number;
  viewCnt: number;
  lectureId: number | null;
  lectureTitle: string | null;
  lecturerId: number | null; // 기존 필드 (하위 호환성)
  lecturerName: string | null; // 기존 필드 (하위 호환성)
  mentorId: number | null; // 신규 필드
  mentorName: string | null; // 신규 필드
  studyCategoryName: string | null;
  chapter?: number; // 챕터 정보 추가
  pinned: boolean;
  exposed: boolean;
  writerName: string;
  writerProfileImageUrl: string | null;
  postDate: string;
  htmlcontent: string;
}

// 페이징 관련 타입 정의
export interface PageableRequest {
  pageNumber: number;
  pageSize: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  offset: number;
  paged: boolean;
  unpaged: boolean;
}

export interface PageableResponse<T> {
  content: T[];
  pageable: PageableRequest;
  totalElements: number;
  totalPages: number;
  last: boolean;
  size: number;
  number: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  numberOfElements: number;
  first: boolean;
  empty: boolean;
}

export interface SearchApiResponse<T> {
  code: number;
  status: string;
  message: string;
  data: PageableResponse<T>;
}

// 아카이브 검색 타입 정의
export interface ArchiveSearchItem {
  id: number;
  title: string;
  category: string;
  viewCnt: number;
  pinned: boolean;
  exposed: boolean;
  writerName: string;
  writerProfileImageUrl: string | null;
  postDate: string;
  htmlcontent: string;
}

export interface SearchResults {
  lectures: LectureSearchItem[];
  lecturers: LecturerSearchItem[];
  posts: PostSearchItem[];
  archives: ArchiveSearchItem[];
}

// 강의 검색 (페이지 지원)
export const searchLecturesWithPage = async (keyword: string, page: number = 0, size: number = 10): Promise<SearchApiResponse<LectureSearchItem>> => {
  try {
    const { data: result } = await apiClient.get<SearchApiResponse<LectureSearchItem>>(
      `/api/search/lectures?keyword=${encodeURIComponent(keyword)}&page=${page}&size=${size}`,
      { withCredentials: true }
    );
    
    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    return result;
  } catch (error) {
    /// console.error('강의 검색 중 오류 발생:', error);
    throw error;
  }
};

// 강의 검색 (기존 호환성 유지)
export const searchLectures = async (keyword: string, limit?: number): Promise<LectureSearchItem[]> => {
  try {
    const result = await searchLecturesWithPage(keyword, 0, limit || 100);
    return result.data.content || [];
  } catch (error) {
    /// console.error('강의 검색 중 오류 발생:', error);
    throw error;
  }
};


// 강연자 검색 (커피챗) - 페이지 지원
export const searchLecturersWithPage = async (keyword: string, page: number = 0, size: number = 10): Promise<SearchApiResponse<LecturerSearchItem>> => {
  try {
    const { data: result } = await apiClient.get<SearchApiResponse<LecturerSearchItem>>(
      `/api/search/mentors?keyword=${encodeURIComponent(keyword)}&page=${page}&size=${size}`,
      { withCredentials: true }
    );

    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    // API 응답의 department 필드를 그대로 사용 (매핑 로직 제거)

    return result;
  } catch (error) {
    /// console.error('강연자 검색 중 오류 발생:', error);
    throw error;
  }
};

// 강연자 검색 (커피챗) - 새로운 API와 기존 API 호환 처리 (내부용)
export const searchLecturersAdvanced = async (
  keyword: string, 
  limit?: number,
  page: number = 0,
  size: number = 10
): Promise<LecturerSearchItem[]> => {
  try {
    // 페이징 파라미터를 포함한 쿼리 문자열 구성
    const params = new URLSearchParams({
      keyword: keyword,
      page: page.toString(),
      size: size.toString()
    });

    let result;
    
    try {
      // 먼저 새로운 API 엔드포인트 시도
      const response = await apiClient.get<SearchApiResponse<LecturerSearchItem>>(
        `/api/search/lecturers?${params}`,
        { withCredentials: true }
      );
      result = response.data;
    } catch (newApiError) {
      /// console.warn('새로운 API 엔드포인트 사용 실패, 기존 API로 대체:', newApiError);
      
      // 새로운 API가 실패하면 기존 API 사용
      const response = await apiClient.get<SearchApiResponse<LecturerSearchItem>>(
        `/api/search/mentors?keyword=${encodeURIComponent(keyword)}`,
        { withCredentials: true }
      );
      result = response.data;

      // API 응답의 department 필드를 그대로 사용 (매핑 로직 제거)
    }
    
    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    return result.data.content || [];
  } catch (error) {
    /// console.error('강연자 검색 중 오류 발생:', error);
    throw error;
  }
};

// 강연자 검색 (커피챗) - 기존 호환성 유지
export const searchLecturers = async (keyword: string, limit?: number): Promise<LecturerSearchItem[]> => {
  try {
    const result = await searchLecturersWithPage(keyword, 0, limit || 100);
    return result.data.content || [];
  } catch (error) {
    /// console.error('강연자 검색 중 오류 발생:', error);
    throw error;
  }
};

// 게시글 검색 (커뮤니티) - 페이징 처리 지원
export const searchPosts = async (keyword: string, limit?: number): Promise<PostSearchItem[]> => {
  try {
    const { data: result } = await apiClient.get<SearchApiResponse<PostSearchItem>>(
      `/api/search/posts?keyword=${encodeURIComponent(keyword)}`,
      { withCredentials: true }
    );
    
    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    // 페이징 처리된 응답에서 content 배열 반환
    const posts = result.data.content || [];
    
    // 제한이 있으면 해당 개수만큼만 반환
    return limit ? posts.slice(0, limit) : posts;
  } catch (error) {
    /// console.error('게시글 검색 중 오류 발생:', error);
    throw error;
  }
};

// 아카이브 검색 - 실제 API 호출
export const searchArchives = async (keyword: string, page: number = 0, size: number = 10): Promise<SearchApiResponse<ArchiveSearchItem>> => {
  try {
    const { data: result } = await apiClient.get<SearchApiResponse<ArchiveSearchItem>>(
      `/api/search/archives?keyword=${encodeURIComponent(keyword)}&page=${page}&size=${size}`,
      { withCredentials: true }
    );
    
    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    return result;
  } catch (error) {
    /// console.error('아카이브 검색 중 오류 발생:', error);
    throw error;
  }
};

// 아카이브 검색 - 기존 호환성 유지
export const searchArchivesSimple = async (keyword: string, limit?: number): Promise<ArchiveSearchItem[]> => {
  try {
    const result = await searchArchives(keyword, 0, limit || 100);
    return result.data.content || [];
  } catch (error) {
    /// console.error('아카이브 검색 중 오류 발생:', error);
    throw error;
  }
};

// 강의별 Q&A 검색 - 페이징 처리 지원
export const searchLectureQnA = async (lectureId: number, keyword: string): Promise<PostSearchItem[]> => {
  try {
    const { data: result } = await apiClient.get<SearchApiResponse<PostSearchItem>>(
      `/api/search/posts/lecture/${lectureId}?keyword=${encodeURIComponent(keyword)}`,
      { withCredentials: true }
    );
    
    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    // 페이징 처리된 응답에서 content 배열 반환
    return result.data.content || [];
  } catch (error) {
    /// console.error('강의 Q&A 검색 중 오류 발생:', error);
    throw error;
  }
};

// 카테고리별 강의 검색 (ClassCategory용)
export const searchLecturesByCategory = async (categoryId: number, keyword: string): Promise<LectureSearchItem[]> => {
  try {
    /// console.log('카테고리별 강의 검색 API 호출:', { categoryId, keyword });
    
    const { data: result } = await apiClient.get<SearchApiResponse<LectureSearchItem>>(
      `/api/search/lectures/${categoryId}?keyword=${encodeURIComponent(keyword)}`,
      { withCredentials: true }
    );
    
    /// console.log('카테고리별 강의 검색 API 응답:', {result});
    
    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    // 페이징 처리된 응답에서 content 배열 반환
    return result.data.content || [];
  } catch (error) {
    /// console.error('카테고리별 강의 검색 중 오류 발생:', error);
    throw error;
  }
};

// 전체 강의 검색 (ClassCategory용)
export const searchAllLectures = async (keyword: string): Promise<LectureSearchItem[]> => {
  try {
    /// console.log('전체 강의 검색 API 호출:', {keyword});
    
    const { data: result } = await apiClient.get<SearchApiResponse<LectureSearchItem>>(
      `/api/search/lectures?keyword=${encodeURIComponent(keyword)}`,
      { withCredentials: true }
    );
    
    /// console.log('전체 강의 검색 API 응답:', {result});
    
    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    // 페이징 처리된 응답에서 content 배열 반환
    return result.data.content || [];
  } catch (error) {
    /// console.error('전체 강의 검색 중 오류 발생:', error);
    throw error;
  }
};

// 스터디 카테고리 검색 - 페이징 처리 지원
export const searchStudyPosts = async (keyword: string): Promise<PostSearchItem[]> => {
  try {
    /// console.log('스터디 검색 API 호출:', {keyword});
    
    const { data: result } = await apiClient.get<SearchApiResponse<PostSearchItem>>(
      `/api/search/posts/5?keyword=${encodeURIComponent(keyword)}`,
      { withCredentials: true }
    );
    
    /// console.log('스터디 검색 API 응답:', {result});
    
    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    // 페이징 처리된 응답에서 content 배열 반환
    return result.data.content || [];
  } catch (error) {
    /// console.error('스터디 검색 중 오류 발생:', error);
    throw error;
  }
};

// 커뮤니티 전체 게시글 검색
export const searchCommunityPosts = async (keyword: string): Promise<PostSearchItem[]> => {
  try {
    /// console.log('커뮤니티 검색 API 호출:', {keyword});
    
    const { data: result } = await apiClient.get<SearchApiResponse<PostSearchItem>>(
      `/api/search/posts?keyword=${encodeURIComponent(keyword)}`,
      { withCredentials: true }
    );
    
    /// console.log('커뮤니티 검색 API 응답:', {result});
    
    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    // 페이징 처리된 응답에서 content 배열 반환
    return result.data.content || [];
  } catch (error) {
    /// console.error('커뮤니티 검색 중 오류 발생:', error);
    throw error;
  }
};

// 강의 질문 카테고리 검색 - 페이징 처리 지원
export const searchLectureQuestionPosts = async (keyword: string): Promise<PostSearchItem[]> => {
  try {
    /// console.log('강의 질문 검색 API 호출:', {keyword});
    
    const { data: result } = await apiClient.get<SearchApiResponse<PostSearchItem>>(
      `/api/search/posts/2?keyword=${encodeURIComponent(keyword)}`,
      { withCredentials: true }
    );
    
    /// console.log('강의 질문 검색 API 응답:', {result});
    
    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    // 페이징 처리된 응답에서 content 배열 반환
    return result.data.content || [];
  } catch (error) {
    /// console.error('강의 질문 검색 중 오류 발생:', error);
    throw error;
  }
};

// 정보공유 게시글 검색 - 페이징 처리 지원
export const searchInfoSharingPosts = async (keyword: string): Promise<PostSearchItem[]> => {
  try {
    /// console.log('정보공유 검색 API 호출:', {keyword});
    
    const { data: result } = await apiClient.get<SearchApiResponse<PostSearchItem>>(
      `/api/search/posts/4?keyword=${encodeURIComponent(keyword)}`,
      { withCredentials: true }
    );
    
    /// console.log('정보공유 검색 API 응답:', {result});
    
    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    // 페이징 처리된 응답에서 content 배열 반환
    return result.data.content || [];
  } catch (error) {
    /// console.error('정보공유 검색 중 오류 발생:', error);
    throw error;
  }
};

// 공지사항 게시글 검색 - 페이징 처리 지원
export const searchNoticePosts = async (keyword: string): Promise<PostSearchItem[]> => {
  try {
    /// console.log('공지사항 검색 API 호출:', {keyword});
    
    const { data: result } = await apiClient.get<SearchApiResponse<PostSearchItem>>(
      `/api/search/posts/3?keyword=${encodeURIComponent(keyword)}`,
      { withCredentials: true }
    );
    
    /// console.log('공지사항 검색 API 응답:', {result});
    
    if (result.code !== 200) {
      throw new Error(`API error: ${result.message}`);
    }

    // 페이징 처리된 응답에서 content 배열 반환
    return result.data.content || [];
  } catch (error) {
    /// console.error('공지사항 검색 중 오류 발생:', error);
    throw error;
  }
};

export interface LecturerSearchResponse {
  code: number;
  status: string;
  message: string;
  data: LecturerSearchItem[];
}



// 모든 페이지의 강의 검색 결과 가져오기
export const searchAllLecturePages = async (keyword: string): Promise<LectureSearchItem[]> => {
  try {
    let allLectures: LectureSearchItem[] = [];
    let currentPage = 0;
    let hasMore = true;

    while (hasMore) {
      const result = await searchLecturesWithPage(keyword, currentPage, 100);
      allLectures = [...allLectures, ...(result.data.content || [])];
      hasMore = !result.data.last;
      currentPage++;
    }

    return allLectures;
  } catch (error) {
    /// console.error('모든 강의 검색 중 오류 발생:', error);
    throw error;
  }
};

// 모든 페이지의 강연자 검색 결과 가져오기
export const searchAllLecturerPages = async (keyword: string): Promise<LecturerSearchItem[]> => {
  try {
    let allLecturers: LecturerSearchItem[] = [];
    let currentPage = 0;
    let hasMore = true;

    while (hasMore) {
      const result = await searchLecturersWithPage(keyword, currentPage, 100);
      allLecturers = [...allLecturers, ...(result.data.content || [])];
      hasMore = !result.data.last;
      currentPage++;
    }

    return allLecturers;
  } catch (error) {
    /// console.error('모든 강연자 검색 중 오류 발생:', error);
    throw error;
  }
};

// 모든 페이지의 아카이브 검색 결과 가져오기
export const searchAllArchivePages = async (keyword: string): Promise<ArchiveSearchItem[]> => {
  try {
    let allArchives: ArchiveSearchItem[] = [];
    let currentPage = 0;
    let hasMore = true;

    while (hasMore) {
      const result = await searchArchives(keyword, currentPage, 100);
      allArchives = [...allArchives, ...(result.data.content || [])];
      hasMore = !result.data.last;
      currentPage++;
    }

    return allArchives;
  } catch (error) {
    /// console.error('모든 아카이브 검색 중 오류 발생:', error);
    throw error;
  }
};

// 통합 검색 (병렬 호출) - 모든 결과 가져오기
export const searchAll = async (keyword: string): Promise<SearchResults> => {
  try {
    const [lectures, lecturers, posts, archives] = await Promise.all([
      searchAllLecturePages(keyword),
      searchAllLecturerPages(keyword),
      searchPosts(keyword, 100),
      searchAllArchivePages(keyword)
    ]);

    return {
      lectures,
      lecturers,
      posts,
      archives
    };
  } catch (error) {
    /// console.error('통합 검색 중 오류 발생:', error);
    throw error;
  }
};
